if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.swatchToMediaMap = []; // Always define it — even if no thumbnails exist
        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.thumbnails) {
          // no thumbnails, but still initialize the core media viewer
          const allMedia = Array.from(this.querySelectorAll('.product__media-item[data-media-id]'));
          this.mediaItems = allMedia;
          this.activeIndex = 0;
          return;
        }

        this.swatchToMediaMap = [];
        const swatches = Array.from(document.querySelectorAll('.product-form__input--pill--color label'));
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));

        swatches.forEach((swatch, index) => {
          const variantName = swatch.title;
          const matchingThumb = thumbnails.find(thumb => {
            const button = thumb.querySelector('button');
            return button?.dataset.variantChoice === variantName;
          });

          if (matchingThumb) {
            this.swatchToMediaMap.push({
              swatchIndex: index,
              mediaId: matchingThumb.dataset.target,
              variantName
            });
          }
        });

        const allMedia = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        this.mediaItems = this.swatchToMediaMap.map(entry => 
          allMedia.find(media => media.dataset.mediaId === entry.mediaId)
        ).filter(Boolean);
        const activeElement = this.mediaItems.find(el => el.classList.contains('is-active'));
        this.activeIndex = this.mediaItems.indexOf(activeElement);
        if (this.activeIndex === -1) this.activeIndex = 0;

        const checkedInput = document.querySelector('.product-form__input--pill--color input[type="radio"]:checked');
        if (checkedInput && this.swatchToMediaMap.length) {
          const variantName = checkedInput.value;
          const index = this.swatchToMediaMap.findIndex(entry => entry.variantName === variantName);
          if (index !== -1) {
            this.goToSlide(index);
          }
        }

        this.dragThreshold = 50;
        this.dragging = false;
        this.currentX = 0;
        this.initialX = 0;
        this.initialY = 0;
        this.xOffset = 0;
        this.isHorizontalDrag = false;

        this.dragStart = this.dragStart.bind(this);
        this.drag = this.drag.bind(this);
        this.dragEnd = this.dragEnd.bind(this);

        this.elements.viewer.addEventListener("touchstart", this.dragStart, false);
        this.elements.viewer.addEventListener("touchend", this.dragEnd, false);
        this.elements.viewer.addEventListener("touchmove", this.drag, false);
        this.elements.viewer.addEventListener("mousedown", this.dragStart, false);
        this.elements.viewer.addEventListener("mouseup", this.dragEnd, false);
        this.elements.viewer.addEventListener("mousemove", this.drag, false);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      goToSlide(index) {
        const mapped = this.swatchToMediaMap[index];
        if (!mapped) return;

        this.activeIndex = index;
        this.setActiveMedia(mapped.mediaId, false);
        this.updateMobilePagination(index);
      }
      
      nextSlide() {
        const nextIndex = (this.activeIndex + 1) % this.swatchToMediaMap.length;
        this.goToSlide(nextIndex);
      }

      previousSlide() {
        const prevIndex = (this.activeIndex - 1 + this.swatchToMediaMap.length) % this.swatchToMediaMap.length;
        this.goToSlide(prevIndex);
      }

      dragStart(e) {
        if (e.type === "touchstart") {
          this.initialX = e.touches[0].clientX - this.xOffset;
          this.initialY = e.touches[0].clientY;
        } else {
          this.initialX = e.clientX - this.xOffset;
          this.initialY = e.clientY;
        }
        this.dragging = true;
        this.isHorizontalDrag = false;
      }

      drag(e) {
        if (this.dragging) {
          let currentX, currentY;
          
          if (e.type === "touchmove") {
            currentX = e.touches[0].clientX - this.initialX;
            currentY = e.touches[0].clientY;
          } else {
            currentX = e.clientX - this.initialX;
            currentY = e.clientY;
          }

          // Calculate the distance moved in both directions
          const deltaX = Math.abs(currentX);
          const deltaY = Math.abs(currentY - this.initialY);

          // Determine if this is primarily a horizontal or vertical gesture
          // Only prevent default if it's clearly a horizontal swipe
          if (!this.isHorizontalDrag && deltaX > 10 && deltaY > 10) {
            // If both movements are significant, check which is dominant
            this.isHorizontalDrag = deltaX > deltaY * 1.5; // Require horizontal to be 1.5x more than vertical
          } else if (!this.isHorizontalDrag && deltaX > 15) {
            // If only horizontal movement is significant
            this.isHorizontalDrag = true;
          }

          // Only handle horizontal drag and prevent default if this is a horizontal gesture
          if (this.isHorizontalDrag) {
            e.preventDefault(); // Only prevent default for horizontal drags
            this.currentX = currentX;
            this.xOffset = this.currentX;
          }
        }
      }

      dragEnd(e) {
        if (!this.dragging) return;
        
        // Only process the movement if it was a horizontal drag
        if (this.isHorizontalDrag) {
          const dragDistance = this.xOffset;
          
          if (dragDistance < (-1 * this.dragThreshold)) {
            this.nextSlide();
          } else if (dragDistance > this.dragThreshold) {
            this.previousSlide();
          }
        }

        // Reset drag-related states
        this.initialX = 0;
        this.initialY = 0;
        this.currentX = 0;
        this.xOffset = 0;
        this.dragging = false;
        this.isHorizontalDrag = false;
      }

      updateMobilePagination(index) {
        const bullets = document.querySelectorAll('.swiper-pagination-bullet');
        bullets.forEach((bullet, i) => {
          if (i === index) {
            bullet.classList.add('swiper-pagination-bullet-active');
            bullet.setAttribute('aria-current', true);
          } else {
            bullet.classList.remove('swiper-pagination-bullet-active');
            bullet.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId, prepend = false, currentVariant) {
        const activeMedia = this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        // Remove active state from all media
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((el) => {
          el.classList.remove('swiper-slide-active', 'is-active');
          el.style.display = 'none';
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
        });

        // Show active media
        activeMedia.style.display = '';
        activeMedia.style.opacity = '1';
        activeMedia.style.visibility = 'visible';
        activeMedia.classList.add('swiper-slide-active', 'is-active');

        // Refresh mediaItems and activeIndex
        const allMedia = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        this.mediaItems = this.swatchToMediaMap.map(entry =>
          allMedia.find(media => media.dataset.mediaId === entry.mediaId)
        ).filter(Boolean);
        this.activeIndex = this.mediaItems.indexOf(activeMedia);

        // Update mobile slider pagination bullets on media change
        this.updateMobilePagination(this.activeIndex);

        // Dispatch event with variantName for swatch update
        let variantName = null;
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          variantName = activeThumbnail?.querySelector('button')?.dataset.variantChoice ?? null;
        }
        this.dispatchEvent(new CustomEvent('media-change', {
          bubbles: true,
          detail: { variantName }
        }));

        /*if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }*/

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          /* Don't scroll up
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });*/
        });

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          const activeIndex = parseInt(activeThumbnail.dataset.mediaPosition) - 1;
          if (document.querySelector('.product_thumbnail-media .swiper-container') && document.querySelector('.product_thumbnail-media .swiper-container').swiper && typeof(document.querySelector('.product_thumbnail-media .swiper-container').swiper.slideTo) === 'function') {
            document.querySelector('.product_thumbnail-media .swiper-container').swiper.slideTo(activeIndex);
          }
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button.thumbnail').forEach(element => {
          element.removeAttribute('aria-current');
        });
        thumbnail.querySelector('button').setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const liveRegion = this.elements.liveRegion;
        if (!liveRegion) return;

        liveRegion.setAttribute('aria-hidden', 'false');
        liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);

        // Hide after a short delay so screen readers re-announce on the next update
        setTimeout(() => {
          liveRegion.setAttribute('aria-hidden', 'true');
          liveRegion.textContent = '';
        }, 2000);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// mobile bullet pagination now chooses variant/updates image
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target.matches('.swiper-pagination-bullet')) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const bullets = Array.from(gallery.querySelectorAll('.swiper-pagination-bullet'));
    const index = bullets.indexOf(target);
    const mapped = gallery.swatchToMediaMap[index];
    if (!mapped) return;

    gallery.setActiveMedia(mapped.mediaId, false);
  });
});